import { useState, type FormEvent } from 'react';
import { qubbase } from '@/lib/qubbase';
import { ThemeSwitcher } from './theme-switcher';
import { Alert, Button, cx, Field, Icon, Input } from './ui';

type Mode = 'sign-in' | 'sign-up' | 'code';

/**
 * Sign in / sign up with qubbase.auth. On success the client stores the session and fires SIGNED_IN,
 * which useSession() picks up — nothing else to wire.
 */
export function AuthScreen() {
  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const attempt = async (key: string, fn: () => Promise<{ error: { message: string } | null }>) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      const { error } = await fn();
      if (error) setError(error.message);
      return !error;
    } finally {
      setBusy(null);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'sign-in') await attempt('submit', () => qubbase.auth.signInWithPassword({ email, password }));
    if (mode === 'sign-up') {
      await attempt('submit', async () => {
        const res = await qubbase.auth.signUp({ email, password, options: { data: { username: username.trim() } } });
        // With email confirmations on, there is no session until the link in the email is followed.
        if (!res.error && !res.data.session) setNotice('Check your inbox to confirm your email, then sign in.');
        return res;
      });
    }
    if (mode === 'code') {
      if (!codeSent) {
        const ok = await attempt('submit', () => qubbase.auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.origin } }));
        if (ok) {
          setCodeSent(true);
          setNotice(`We emailed a 6-digit code (and a magic link) to ${email}.`);
        }
      } else await attempt('submit', () => qubbase.auth.verifyOtp({ email, token: code.trim(), type: 'email' }));
    }
  };

  const switchMode = (m: Mode) => {
    setMode(m);
    setError(null);
    setNotice(null);
    setCodeSent(false);
  };

  return (
    <div className="flex min-h-screen flex-col">
      <header className="flex items-center justify-between px-6 py-4">
        <Brand />
        <ThemeSwitcher />
      </header>
      <main className="flex flex-1 items-center justify-center px-4 pb-16">
        <div className="w-full max-w-sm">
          <h1 className="text-2xl font-bold tracking-tight">{mode === 'sign-up' ? 'Create your account' : 'Welcome back'}</h1>
          <p className="mt-1.5 text-sm text-muted">A shared task board built on QubBase: auth, database, storage, functions and realtime.</p>

          <div className="mt-6 grid grid-cols-3 rounded-lg border border-line bg-sunken p-0.5 text-xs font-medium">
            {(
              [
                ['sign-in', 'Password'],
                ['sign-up', 'Sign up'],
                ['code', 'Email code'],
              ] as const
            ).map(([m, label]) => (
              <button
                key={m}
                type="button"
                onClick={() => switchMode(m)}
                className={cx('rounded-md py-1.5 transition', mode === m ? 'bg-surface text-ink shadow-sm ring-1 ring-line' : 'text-muted hover:text-ink')}
              >
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-5 flex flex-col gap-4">
            {error && <Alert>{error}</Alert>}
            {notice && <Alert tone="info">{notice}</Alert>}
            {mode === 'sign-up' && (
              <Field label="Username">
                <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="ada" maxLength={40} required />
              </Field>
            )}
            <Field label="Email">
              <Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required disabled={mode === 'code' && codeSent} />
            </Field>
            {mode !== 'code' && (
              <Field label="Password">
                <Input
                  type="password"
                  autoComplete={mode === 'sign-up' ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  required
                />
              </Field>
            )}
            {mode === 'code' && codeSent && (
              <Field label="6-digit code">
                <Input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" required />
              </Field>
            )}
            <Button type="submit" variant="primary" loading={busy === 'submit'}>
              {mode === 'sign-in' ? 'Sign in' : mode === 'sign-up' ? 'Create account' : codeSent ? 'Verify code' : 'Email me a code'}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-muted">
            <span className="h-px flex-1 bg-line" /> or <span className="h-px flex-1 bg-line" />
          </div>
          <Button className="w-full" loading={busy === 'anon'} onClick={() => attempt('anon', () => qubbase.auth.signInAnonymously())}>
            <Icon name="user" /> Continue as guest
          </Button>
          <p className="mt-3 text-center text-xs text-muted">Guests are real (anonymous) users: they can add tasks, which stay theirs until they sign out.</p>
        </div>
      </main>
    </div>
  );
}

export function Brand() {
  return (
    <div className="flex items-center gap-2 font-semibold">
      <span className="inline-flex size-7 items-center justify-center rounded-lg bg-brand text-brand-ink">
        <Icon name="check" className="size-4" />
      </span>
      QubBase Tasks
    </div>
  );
}
